// Checkbox.jsx
import React from "react";

/**
 * A reusable checkbox component with single or multiple selection support
 * @param {Object} props
 * @param {boolean} props.checked - Whether the checkbox is checked
 * @param {Function} props.onChange - Callback when checkbox state changes
 * @param {string} props.label - Optional label text
 * @param {string} props.id - Unique identifier for the checkbox
 * @param {string} props.name - Name attribute for form submission
 * @param {boolean} props.disabled - Whether the checkbox is disabled
 * @param {string} props.size - Size of the checkbox ('sm', 'md', 'lg')
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.labelProps - Props for the label element
 * @param {string} props.selectionMode - 'single' or 'multiple' (default: 'multiple')
 * @param {boolean} props.showLabel - Whether to show the label (default: true)
 */
const Checkbox = ({
  checked,
  onChange,
  label,
  id,
  name,
  disabled = false,
  size = "md",
  className = "",
  labelProps = {},
  selectionMode = "multiple",
  showLabel = true,
  ...rest
}) => {
  const handleChange = (e) => {
    if (disabled) return;
    
    if (selectionMode === "single") {
      // For single selection, we always set to true when clicked
      onChange(true, e);
    } else {
      // For multiple selection, toggle the state
      onChange(!checked, e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleChange(e);
    }
  };

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const labelSizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
  };

  return (
    <div className={`flex items-center ${className}`}>
<div 
  role="checkbox"
  aria-checked={checked}
  tabIndex={disabled ? -1 : 0}
  onKeyDown={handleKeyDown}
  onClick={handleChange}
  className={`relative ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  aria-disabled={disabled}
>
        {/* Unchecked state */}
        {!checked && (
          <svg
            className={sizeClasses[size]}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="19"
              height="19"
              rx="5.5"
              stroke="black"
              strokeOpacity="0.24"
            />
          </svg>
        )}

        {/* Checked state */}
        {checked && (
          <svg
            className={sizeClasses[size]}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="20" height="20" rx="6" fill="#F0F0F0" />
            <rect
              x="0.5"
              y="0.5"
              width="19"
              height="19"
              rx="5.5"
              stroke="black"
              strokeOpacity="0.24"
            />
            <mask
              id={`mask-${id}`}
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="20"
              height="20"
            >
              <rect width="20" height="20" fill="#D9D9D9" />
            </mask>
            <g mask={`url(#mask-${id})`}>
              <path
                d="M7.96094 15.0013L3.21094 10.2513L4.39844 9.0638L7.96094 12.6263L15.6068 4.98047L16.7943 6.16797L7.96094 15.0013Z"
                fill="#52525B"
                fillOpacity="0.84"
              />
            </g>
          </svg>
        )}

        {/* Hidden input for form submission */}
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          aria-hidden="true"
          {...rest}
        />
      </div>

      {showLabel && label && (
        <label
          htmlFor={id}
          className={`cursor-pointer ml-2 select-none ${labelSizeClasses[size]} ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={(e) => {
            e.preventDefault();
            handleChange(e);
          }}
          {...labelProps}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default Checkbox;